import React, { useContext, useEffect, useState } from "react";
import CaptchaFrame, {
  captchaFallbackReason,
  getCaptchaConfig
} from "../../components/CaptchaFrame/CaptchaFrame";
import AuthErrorNotice, { getHotelContact } from "./AuthErrorNotice";
import MagicLinkSent from "./MagicLinkSent";
import { getAuthErrorInfo } from "../../helpers/authErrors";
import "./LoginPage.css";
import { GeneralContext } from "../../contexts/GeneralContext";
import {
  Grid,
  Box,
  Typography,
  Divider,
  TextField,
  Button,
  Container,
  CircularProgress
} from "@material-ui/core";
import { FormattedMessage } from "react-intl";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import { picsUrl } from "../../helpers/helpers";
import { useHistory } from "react-router-dom";
import {
  peekLoginDestination,
  takeLoginDestination
} from "../../helpers/loginDestination";

// Cada fallo que se cuenta ya incluye una recarga automática del iframe
// (CaptchaFrame).
const CAPTCHA_MAX_FAILURES = 3;
// Igual que el cooldown del magic link en api2: antes de eso un nuevo pedido no reenvía.
export const MAGIC_LINK_RETRY_SECONDS = 30;

const LoginPage = () => {
  const {
    ProductData,
    PosData,
    doAuth,
    doRequestMagicLink,
    Authenticated,
    setAuthenticated,
    AuthNotice,
    setAuthNotice,
    LoginPrefill,
    setLoginPrefill,
    CurrentPos,
    CurrentLng
  } = useContext(GeneralContext);
  const [LastName, setLastName] = useState(
    LoginPrefill ? LoginPrefill.lastname : ""
  );
  const [CID, setCID] = useState(LoginPrefill ? LoginPrefill.cid : "");
  // Error de la acción anterior ({ code, status, retryAfter }); también llega
  // el de un link de acceso (?t=) que no se pudo canjear.
  const [Notice, setNotice] = useState(AuthNotice);
  const [CaptchaToken, setCaptchaToken] = useState(null);
  const [CaptchaResetKey, setCaptchaResetKey] = useState(0);
  const [CaptchaFailures, setCaptchaFailures] = useState(0);
  // Código del último fallo del captcha (lo que llega por onError).
  const [CaptchaLastError, setCaptchaLastError] = useState(null);
  const captchaEnabled = Boolean(getCaptchaConfig());
  const captchaPos = CurrentPos || localStorage.getItem("CurrentPos") || "";
  const captchaLng = ["es", "en", "pt"].includes(CurrentLng) ? CurrentLng : "es";
  // La empresa sale siempre del POS (el ?c= de los links viejos se ignora).
  const companyId = PosData.CompanyPos;
  const [FormErrors, setFormErrors] = useState({ LastName: false, CID: false });
  const [RestorePassFormErrors, setRestorePassFormErrors] = useState({
    CID: false
  });
  const [Loading, setLoading] = useState(false);
  const [restorePassword, setRestorePassword] = useState(false);
  // Reserva a la que se pidió el enlace (muestra la pantalla de confirmación) y cuándo se
  // puede volver a pedir.
  const [MagicLinkSentFor, setMagicLinkSentFor] = useState(null);
  const [RetryAt, setRetryAt] = useState(0);
  const [Now, setNow] = useState(Date.now());
  const history = useHistory();

  const retrySecondsLeft = Math.max(0, Math.ceil((RetryAt - Now) / 1000));
  useEffect(() => {
    if (!MagicLinkSentFor || RetryAt <= Date.now()) return undefined;
    const timer = setInterval(() => {
      const now = Date.now();
      setNow(now);
      if (now >= RetryAt) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [MagicLinkSentFor, RetryAt]);

  useEffect(() => {
    if (Authenticated) {
      history.push("/app");
    }
    // Los datos precargados y el aviso se consumen una vez.
    if (AuthNotice) setAuthNotice(null);
    if (LoginPrefill) setLoginPrefill(null);
    return () => {
      setLastName("");
      setCID("");
      setFormErrors({ LastName: false, CID: false });
      setRestorePassFormErrors({ CID: false });
    };
  }, []);

  const loginFormValid = () => {
    setFormErrors({ LastName: !Boolean(LastName), CID: !Boolean(CID) });
    if (!FormErrors.LastName && !FormErrors.CID) return true;
    return false;
  };
  const restorePassFormValid = () => {
    setRestorePassFormErrors({ CID: !Boolean(CID) });
    if (!RestorePassFormErrors.CID) return true;
    return false;
  };

  const decodeStr = str => {
    if (typeof str !== "string") return str;
    let parser = new DOMParser();
    let dom = parser.parseFromString(
      "<!doctype html><body>" + str,
      "text/html"
    );
    return dom.body.textContent;
  };

  const loginKeyPress = event => {
    if (event.key === "Enter") {
      authorize();
    }
  };

  const restorePassKeyPress = event => {
    if (event.key === "Enter") {
      sendMagicLink();
    }
  };

  // Los tokens del captcha son de un solo uso: después de CADA intento (bien o
  // mal) se pide uno nuevo.
  const resetCaptcha = () => {
    setCaptchaToken(null);
    setCaptchaResetKey(key => key + 1);
  };
  // Si el captcha falla 3 veces seguidas (no carga, timeout, error) se permite
  // enviar SIN token: api2 decide (enforce -> captcha_required/unavailable,
  // log -> pasa). Los botones nunca quedan deshabilitados para siempre.
  const captchaUnavailable = CaptchaFailures >= CAPTCHA_MAX_FAILURES;
  const canSubmit = !captchaEnabled || Boolean(CaptchaToken) || captchaUnavailable;
  // Token o, si se sale sin él, el motivo (`captcha_fallback`) para que api2
  // lo registre. Nunca los dos juntos. Va en el cuerpo y no en un header: un
  // header propio dispararía un preflight de CORS.
  const withCaptcha = body => {
    if (CaptchaToken) body.captcha_token = CaptchaToken;
    else if (!captchaEnabled) body.captcha_fallback = "not_configured";
    else if (captchaUnavailable)
      body.captcha_fallback = captchaFallbackReason(CaptchaLastError);
    return body;
  };

  const authorize = () => {
    if (!canSubmit) return;
    if (loginFormValid()) {
      setLoading(true);
      setNotice(null);
      const body = withCaptcha({
        reserva: CID.trim(),
        apellido: LastName.trim(),
        companyid: companyId
      });
      doAuth(body, true)
        .then(res => {
          document.body.scrollTop = 0; // For Safari
          document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
          setAuthenticated(true);
          setLoading(false);
          // A la cancelación si se venía de ahí; si no, al check-in. Con el
          // número interno: el huésped pudo escribir el código del canal.
          const cid = (res && res.userData && res.userData.CID) || CID.trim();
          history.push("/app/" + takeLoginDestination() + "/" + cid);
        })
        .catch(err => {
          setNotice(getAuthErrorInfo(err));
          setAuthenticated(false);
          setLoading(false);
          resetCaptcha();
        });
    }
  };

  const sendMagicLink = () => {
    if (!canSubmit) return;
    if (restorePassFormValid()) {
      setLoading(true);
      setNotice(null);
      const body = { cid: CID.trim(), company_id: companyId };
      // El enlace por email lleva a la cancelación si se venía de ahí (api2).
      if (peekLoginDestination() === "cancel") body.purpose = "cancel";
      doRequestMagicLink(withCaptcha(body))
        .then(() => {
          document.body.scrollTop = 0; // For Safari
          document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
          setMagicLinkSentFor(CID.trim());
          setRetryAt(Date.now() + MAGIC_LINK_RETRY_SECONDS * 1000);
          setNow(Date.now());
          setLoading(false);
          resetCaptcha();
        })
        .catch(err => {
          setNotice(getAuthErrorInfo(err));
          setLoading(false);
          resetCaptcha();
        });
    }
  };

  const toggleRestorePassword = () => {
    setRestorePassword(!restorePassword);
    setMagicLinkSentFor(null);
    setNotice(null);
    // Cada formulario usa su propio captcha (action login / magic_link).
    setCaptchaToken(null);
  };

  const captchaBlock = action => (
    <Grid item xs={12}>
      <CaptchaFrame
        key={action}
        action={action}
        pos={captchaPos}
        lng={captchaLng}
        resetKey={CaptchaResetKey}
        onToken={token => {
          setCaptchaToken(token);
          if (token) setCaptchaFailures(0);
        }}
        onError={code => {
          setCaptchaLastError(code);
          setCaptchaFailures(n => n + 1);
        }}
        canContinue={captchaUnavailable}
      />
    </Grid>
  );

  const noticeBlock = (
    <AuthErrorNotice
      info={Notice}
      PosData={PosData}
      ProductData={ProductData}
      showMagicLinkOffer={!restorePassword}
      onUseMagicLink={toggleRestorePassword}
    />
  );

  // Cabecera: la foto del producto. Un POS sin producto por defecto (p. ej. un
  // marketplace como convenios.amerian.com) no la tiene: se usa la imagen de
  // cabecera o el logo del POS, y si no hay ninguna no se muestra.
  const heroWidth = Math.min(600, window.outerWidth);
  const heroImage = ProductData.foto_tapa
    ? { src: picsUrl(ProductData.foto_tapa, heroWidth, heroWidth * 0.75), logo: false }
    : PosData.HeaderImage
    ? { src: picsUrl(PosData.HeaderImage), logo: false }
    : PosData.Logo
    ? { src: picsUrl(PosData.Logo), logo: true }
    : null;

  return (
    <Grid
      container
      direction={"column"}
      alignItems="stretch"
      className={"LoginPage"}
      style={{ width: "100%" }}
    >
      {heroImage && (
        <Grid item style={{ height: "30vh", maxHeight: "20em" }}>
          <img
            src={heroImage.src}
            alt=""
            className={
              "pxwm-loginhero" + (heroImage.logo ? " pxwm-loginhero--logo" : "")
            }
          />
        </Grid>
      )}
      <Grid item xs>
        <Box p={2} pt={4} pb={4}>
          <Typography align="center" variant="h5" component="h1" gutterBottom>
            <strong>
              <FormattedMessage
                values={{
                  productTitle: decodeStr(
                    (ProductData.additional_data &&
                      ProductData.additional_data.title) ||
                      PosData.Title ||
                      ""
                  )
                }}
                id="login.title"
              />
            </strong>
          </Typography>
          <Typography align="center" variant="subtitle1" color="textSecondary">
            <FormattedMessage id="login.intro" />
          </Typography>
        </Box>
      </Grid>
      <Divider variant="middle" />
      <form>
        <Grid item xs>
          {restorePassword && MagicLinkSentFor ? (
            <MagicLinkSent
              cid={MagicLinkSentFor}
              contact={getHotelContact(PosData, ProductData)}
              secondsLeft={retrySecondsLeft}
              onRetry={() => {
                // Vuelve al formulario con la reserva cargada (se puede corregir) y un
                // captcha nuevo.
                setMagicLinkSentFor(null);
                setNotice(null);
                resetCaptcha();
              }}
              onBack={toggleRestorePassword}
            />
          ) : restorePassword ? (
            <Box p={3}>
              <Box pb={3}>
                <Typography align="center" variant="body1">
                  <FormattedMessage id="login.restorePassword" />
                </Typography>
              </Box>
              <Container maxWidth="sm">
                {noticeBlock}
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label={<FormattedMessage id="login.cid" />}
                      value={CID}
                      error={RestorePassFormErrors.CID}
                      onKeyPress={restorePassKeyPress}
                      onBlur={restorePassFormValid}
                      onInput={e => {
                        setCID(e.target.value);
                      }}
                      variant="outlined"
                      name="password"
                    />
                  </Grid>
                  {captchaBlock("magic_link")}
                  <Grid item xs={12} className="ButtonWrapper">
                    <Box>
                      <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        fullWidth
                        disabled={Loading || !canSubmit}
                        endIcon={<ChevronRightIcon />}
                        onClick={() => sendMagicLink()}
                      >
                        <FormattedMessage id="login.sendMagicLink" />
                      </Button>
                      {Loading && (
                        <CircularProgress
                          size={24}
                          className="ButtonProgress"
                        />
                      )}
                    </Box>
                  </Grid>
                </Grid>
                <Grid item xs={12} className="ButtonWrapper">
                  <Box mt={2}>
                    <Button
                      variant="text"
                      color="primary"
                      size="large"
                      fullWidth
                      disabled={Loading}
                      startIcon={<ChevronLeftIcon />}
                      onClick={toggleRestorePassword}
                    >
                      <FormattedMessage id="login.toggleRestorePasswordBack" />
                    </Button>
                  </Box>
                </Grid>
              </Container>
            </Box>
          ) : (
            <Box p={3}>
              <Box pb={3}>
                <Typography align="center" variant="body1">
                  <FormattedMessage id="login.formIntro" />
                </Typography>
              </Box>
              <Container maxWidth="sm">
                {noticeBlock}
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      autoFocus
                      required
                      label={<FormattedMessage id="login.lastname" />}
                      value={LastName}
                      error={FormErrors.LastName}
                      onKeyPress={loginKeyPress}
                      inputProps={{ autoComplete: "family-name" }}
                      onInput={e => {
                        setLastName(e.target.value);
                      }}
                      variant="outlined"
                      name="username"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label={<FormattedMessage id="login.cid" />}
                      value={CID}
                      error={FormErrors.CID}
                      onKeyPress={loginKeyPress}
                      onBlur={loginFormValid}
                      onInput={e => {
                        setCID(e.target.value);
                      }}
                      variant="outlined"
                      name="password"
                    />
                  </Grid>
                  {captchaBlock("login")}
                  <Grid item xs={12} className="ButtonWrapper">
                    <Box>
                      <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        fullWidth
                        disabled={Loading || !canSubmit}
                        endIcon={<ChevronRightIcon />}
                        onClick={() => authorize()}
                      >
                        <FormattedMessage id="login.submit" />
                      </Button>
                      {Loading && (
                        <CircularProgress
                          size={24}
                          className="ButtonProgress"
                        />
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} className="ButtonWrapper">
                    <Box>
                      <Button
                        variant="text"
                        color="primary"
                        size="large"
                        fullWidth
                        disabled={Loading}
                        endIcon={<ChevronRightIcon />}
                        onClick={toggleRestorePassword}
                      >
                        <FormattedMessage id="login.toggleRestorePassword" />
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Container>
            </Box>
          )}
        </Grid>
      </form>
    </Grid>
  );
};

export default LoginPage;
